/**
 * Menu principal : comportement bureau.
 *
 * Intention d'ouverture : 120 ms avant d'ouvrir, 180 ms de sursis avant de
 * fermer. C'est le defaut le plus courant et le plus penible : sans le sursis,
 * le panneau se ferme des que le curseur traverse le vide entre le declencheur
 * et le panneau. Avec un panneau deja ouvert, passer sur une autre entree
 * bascule le contenu instantanement, sans fermeture intermediaire.
 */

const ENTER_DELAY = 120;
const LEAVE_GRACE = 180;
// La meme valeur que `.lrs-mm__panel--mini` dans la feuille : les deux doivent
// rester d'accord, sinon le panneau simple deborde a droite de la fenetre.
// Passee de 320 a 360 au lot 16 : le pied du panneau porte maintenant trois
// mentions de reassurance et un libelle de sortie nomme.
const MINI_WIDTH = 360;
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

class MegaMenu {
  constructor(root) {
    this.root = root;
    this.items = Array.from(root.querySelectorAll('[data-lrs-mm-item]'))
      .map((item) => ({
        item,
        trigger: item.querySelector('[data-lrs-mm-trigger]'),
        panel: item.querySelector('[data-lrs-mm-panel]'),
      }))
      .filter((entry) => entry.trigger && entry.panel);

    this.current = null;
    this.enterTimer = 0;
    this.leaveTimer = 0;

    this.items.forEach((entry) => this.bind(entry));
    this.bindGlobal();
  }

  get fine() {
    return window.matchMedia(FINE_POINTER).matches;
  }

  bind({ item, trigger, panel }) {
    const entry = { item, trigger, panel };

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch' || !this.fine) return;
      this.cancelLeave();
      // Un panneau est deja ouvert : on bascule sans attendre ni fermer.
      if (this.current && this.current !== entry) {
        this.show(entry);
        return;
      }
      this.scheduleOpen(entry);
    });

    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch' || !this.fine) return;
      this.cancelOpen();
      this.scheduleClose();
    });

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.cancelOpen();
      this.cancelLeave();
      if (this.current === entry) {
        this.hide();
        return;
      }
      this.show(entry);
    });

    // Echap ferme et rend le focus au declencheur.
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.current !== entry) return;
      event.stopPropagation();
      this.hide();
      trigger.focus();
    });

    // Tab qui sort de l'entree ferme le panneau : le focus ne doit jamais
    // rester dans un panneau invisible.
    item.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        if (this.current !== entry) return;
        if (item.contains(document.activeElement)) return;
        this.hide();
      });
    });
  }

  bindGlobal() {
    document.addEventListener('pointerdown', (event) => {
      if (!this.current) return;
      if (this.current.item.contains(event.target)) return;
      this.hide();
    });

    window.addEventListener(
      'scroll',
      () => {
        if (this.current) this.hide();
      },
      { passive: true }
    );

    window.addEventListener('resize', () => {
      if (this.current) this.place(this.current);
    });

    // Navigation : le panneau ne doit pas survivre au depart de la page.
    window.addEventListener('pagehide', () => this.hide());
  }

  scheduleOpen(entry) {
    this.cancelOpen();
    this.enterTimer = window.setTimeout(() => this.show(entry), ENTER_DELAY);
  }

  scheduleClose() {
    this.cancelLeave();
    this.leaveTimer = window.setTimeout(() => this.hide(), LEAVE_GRACE);
  }

  cancelOpen() {
    window.clearTimeout(this.enterTimer);
    this.enterTimer = 0;
  }

  cancelLeave() {
    window.clearTimeout(this.leaveTimer);
    this.leaveTimer = 0;
  }

  place({ trigger, panel }) {
    const header = this.root.closest('.shopify-section, header') || this.root;
    const mini = panel.classList.contains('lrs-mm__panel--mini');

    // `header-component` porte `contain: layout`, ce qui en fait le bloc
    // conteneur des descendants en position fixe : `top: 149px` n'y vaut plus
    // 149 px depuis le haut de la fenetre. Plutot que de deviner le repere, on
    // pose l'origine a zero, on mesure ou elle tombe, et on corrige de l'ecart.
    if (!mini) panel.style.width = `${window.innerWidth}px`;
    panel.style.top = '0px';
    panel.style.left = '0px';

    const origin = panel.getBoundingClientRect();
    const wantedTop = header.getBoundingClientRect().bottom;
    // `ceil` et non `round` : arrondi vers le bas, un demi-pixel de page
    // transparait sous la bordure de l'en-tete. Vers le haut, le panneau
    // recouvre cette fraction et rien ne se voit.
    panel.style.top = `${Math.ceil(wantedTop - origin.top)}px`;

    let wantedLeft = 0;
    if (mini) {
      const max = window.innerWidth - MINI_WIDTH - 16;
      wantedLeft = Math.max(16, Math.min(trigger.getBoundingClientRect().left, max));
    }
    panel.style.left = `${Math.round(wantedLeft - origin.left)}px`;
  }

  show(entry) {
    this.cancelOpen();
    this.cancelLeave();

    if (this.current && this.current !== entry) this.close(this.current);

    entry.panel.hidden = false;
    this.place(entry);
    // Un cadre de retard : `hidden` retire, la transition peut alors jouer.
    window.requestAnimationFrame(() => {
      entry.panel.classList.add('is-open');
    });
    entry.trigger.setAttribute('aria-expanded', 'true');
    this.current = entry;
  }

  close(entry) {
    entry.panel.classList.remove('is-open');
    entry.panel.hidden = true;
    entry.trigger.setAttribute('aria-expanded', 'false');
  }

  hide() {
    this.cancelOpen();
    this.cancelLeave();
    if (!this.current) return;
    this.close(this.current);
    this.current = null;
  }
}

function scan(root) {
  (root || document).querySelectorAll('lrs-mega-menu').forEach((el) => {
    if (el.dataset.lrsMm === 'on') return;
    el.dataset.lrsMm = 'on';
    new MegaMenu(el);
  });
}

scan(document);
document.addEventListener('shopify:section:load', (event) => scan(event.target));
